import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '@/shared/ui/Page';
import { PageHeader } from '@/shared/ui/PageHeader';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { subscriptionApi } from '@/entities/subscription/api';
import { ledgerApi } from '@/entities/ledger-operation/api';
import { OperationRow } from '@/entities/ledger-operation/OperationRow';
import { amountText, toNumber } from '@/shared/lib/money';
import { cn } from '@/shared/lib/cn';
import { useT } from '@/shared/lib/i18n';

export function MemberDetail() {
  const { id, membershipId } = useParams<{ id: string; membershipId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const t = useT();

  const detailQ = useQuery({
    queryKey: ['subscription', id],
    queryFn: () => subscriptionApi.getDetail(id!),
    enabled: !!id,
  });
  const membersQ = useQuery({
    queryKey: ['subscription-members', id],
    queryFn: () => subscriptionApi.getMembers(id!),
    enabled: !!id,
  });

  const exitRequestsQ = useQuery({
    queryKey: ['subscription-exit-requests', id],
    queryFn: () => subscriptionApi.listExitRequests(id!),
    enabled: !!id,
  });

  const matchingExitRequest = useMemo(
    () => exitRequestsQ.data?.find((er) => er.membership_id === membershipId),
    [exitRequestsQ.data, membershipId],
  );

  const declineExitM = useMutation({
    mutationFn: () => subscriptionApi.declineExit(matchingExitRequest!.exit_request_id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['subscription-members', id] });
      await qc.invalidateQueries({ queryKey: ['subscription-exit-requests', id] });
    },
  });

  const member = useMemo(
    () => membersQ.data?.items.find((item) => item.membership_id === membershipId),
    [membersQ.data?.items, membershipId],
  );

  const operationsQ = useQuery({
    queryKey: ['subscription-operations', id, member?.user_id],
    queryFn: () => ledgerApi.listOperations(id!, { participant_id: member!.user_id, limit: 20 }),
    enabled: !!id && !!member,
  });

  const removeM = useMutation({
    mutationFn: () => subscriptionApi.removeMember(membershipId!),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['subscription-members', id] });
      await qc.invalidateQueries({ queryKey: ['subscription', id] });
      navigate(`/subscriptions/${id}`);
    },
  });

  const sub = detailQ.data?.subscription;
  const balance = toNumber(member?.balance) ?? 0;

  return (
    <Page>
      <PageHeader title={<span className="font-bold text-[19px]">{member?.display_name ?? t('management.member.title')}</span>} />
      <div className="px-[18px] pb-5 flex flex-col gap-4 min-h-0">
        <div className={cn(
          'rounded-[18px] p-4 border',
          balance > 0
            ? 'bg-[rgba(230,103,53,0.13)] border-[rgba(230,103,53,0.3)]'
            : 'bg-[rgba(0,153,40,0.1)] border-brand-green/30',
        )}>
          <div className="flex items-center gap-2 text-[13px] text-text-secondary">
            <span className="w-[22px] h-[22px] rounded-[6px] bg-brand-gradient text-white text-[10px] font-bold flex items-center justify-center">
              {sub?.name.trim()[0]?.toUpperCase() ?? '?'}
            </span>
            {t('management.member.balance', { name: sub?.name ?? t('subscription.title') })}
          </div>
          <div className={cn('text-[36px] font-bold font-mono leading-[1.05] mt-1', balance > 0 ? 'text-status-warn' : 'text-status-paid')}>
            {balance > 0 ? '-' : ''}{amountText(balance, sub?.currency ?? 'RUB')}
          </div>
          {member && (
            <div className="mt-2">
              <StatusBadge
                variant={member.status === 'pending_exit' ? 'confirm' : 'active'}
                label={member.status === 'pending_exit' ? t('management.member.exitRequested') : t('subscription.status.active')}
              />
            </div>
          )}
        </div>

        {matchingExitRequest && (
          <div className="grid grid-cols-2 gap-2">
            <button
              className="rounded-[13px] py-3 text-[15px] font-semibold text-white bg-brand-gradient shadow-[0_8px_18px_rgba(18,120,90,0.28)]"
              onClick={() => navigate(`/subscriptions/${id}/exit-requests/${matchingExitRequest.exit_request_id}/approve`)}
            >
              {t('management.member.exitApprove')}
            </button>
            <button
              className="rounded-[13px] py-3 text-[15px] font-semibold bg-[rgba(244,251,245,0.55)] border border-white/60 disabled:opacity-50"
              disabled={declineExitM.isPending}
              onClick={() => declineExitM.mutate()}
            >
              {t('management.member.exitDecline')}
            </button>
          </div>
        )}

        <p className="text-[14px] font-semibold text-text-secondary">{t('subscription.memberDetail.operations')}</p>
        <div className="rounded-[16px] px-4 py-1 bg-[rgba(244,251,245,0.5)] border border-white/55">
          {operationsQ.data?.items.map((op) => <OperationRow key={op.id} op={op} compact />)}
          {!operationsQ.isLoading && (operationsQ.data?.items.length ?? 0) === 0 && (
            <p className="text-center text-[14px] text-text-secondary py-6">{t('operation.empty')}</p>
          )}
        </div>

        <div className="mt-auto grid grid-cols-2 gap-2">
          <button
            className="rounded-[11px] py-[10px] text-[13px] font-semibold text-brand-green bg-white/45 border border-white/60"
            onClick={() => navigate(`/subscriptions/${id}/members/${membershipId}/correction`)}
          >
            {t('management.member.adjust')}
          </button>
          <button
            className="rounded-[11px] py-[10px] text-[13px] font-semibold text-status-debt bg-[rgba(229,42,48,0.08)] border border-[rgba(193,7,16,0.35)] disabled:opacity-50"
            disabled={removeM.isPending || !member}
            onClick={() => removeM.mutate()}
          >
            {t('management.member.remove')}
          </button>
        </div>
      </div>
    </Page>
  );
}
